import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  openDialog,
  showNotification,
} from 'platform/components';
import {Box, Show, VStack} from 'platform/foundation';

import {useImperativeHandle, useRef} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isNotNilOrEmpty, toNumber} from 'ramda-adjunct';

import {useLazyCalculateFromWithoutVatQuery} from '@dms/api/core';
import {CountryAlpha3CodeEnum, VatRateType} from '@dms/api/shared';
import {ListVehicleCostCategoriesApiResponse, SaleVehicleResponseBody} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {CurrencyCodeType, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {handleApiError} from '../../utils/handleApiError';
import {CalculateFinancingForm} from '../CalculateFinancingForm/CalculateFinancingForm';
import {FormVatPriceField} from './components/FormVatPriceField';
import {
  BaseTransactionFormSchema,
  BaseTransactionFormSchemaType,
} from './VehicleTransactionFormSchema';

export type VehicleSimplifiedTransactionFormRef = {
  formApi: UseFormReturn<BaseTransactionFormSchemaType>;
};

type VehicleSimplifiedTransactionFormProps = RequiredTestIdProps & {
  ref?: React.Ref<VehicleSimplifiedTransactionFormRef>;
  onSubmit: (data: BaseTransactionFormSchemaType) => Promise<void>;
  onDiscard?: VoidFunction;
  values?: BaseTransactionFormSchemaType;
  currency: CurrencyCodeType;
  countryCode: CountryAlpha3CodeEnum;
  canSetExpectedPrice?: boolean;
  saleVehicle?: SaleVehicleResponseBody;
} & (
    | {showCategories: true; categories: ListVehicleCostCategoriesApiResponse}
    | {showCategories?: false; categories?: never}
  );

export function VehicleSimplifiedTransactionForm(props: VehicleSimplifiedTransactionFormProps) {
  const {ref, ...otherProps} = props;
  const isEditForm = isNotNilOrEmpty(otherProps.values);
  const defaultValues = isNotNilOrEmpty(otherProps.values) ? otherProps.values : undefined;
  const formApiRef = useRef<UseFormReturn<BaseTransactionFormSchemaType>>(null);

  const [calculatePriceWithoutVat] = useLazyCalculateFromWithoutVatQuery();

  useImperativeHandle(ref, () => ({
    formApi: formApiRef.current as UseFormReturn<BaseTransactionFormSchemaType>,
  }));

  return (
    <Form<BaseTransactionFormSchemaType>
      onSubmit={otherProps.onSubmit}
      defaultValues={{
        name: '',
        expectedPrice: null,
        category: null,
        ...defaultValues,
      }}
      experimentalZodSchema={BaseTransactionFormSchema}
    >
      {(control, formApi) => {
        formApiRef.current = formApi;

        const isCalculatingVat = formApi.watch('expectedPrice.isCalculatingVat') ?? false;

        return (
          <VStack spacing={4}>
            <Show when={otherProps.showCategories && isNotNilOrEmpty(otherProps.categories)}>
              <FormField
                type="chips"
                isDeselectable
                name="category"
                label={i18n.t('entity.vehicle.costs.category')}
                control={control}
                data-testid={suffixTestId('category', otherProps)}
                options={
                  otherProps.categories?.map((category) => ({
                    value: category.key,
                    label: category.name,
                  })) ?? []
                }
              />
            </Show>
            <FormField
              type="text"
              control={control}
              name="name"
              isRequired
              label={i18n.t('general.labels.name')}
              data-testid={suffixTestId('name', otherProps)}
            />
            <FormVatPriceField
              control={control}
              name="expectedPrice"
              data-testid="expectedPrice"
              currency={otherProps.currency}
              countryCode={otherProps.countryCode}
              isDisabled={!otherProps.canSetExpectedPrice}
              tooltips={
                !otherProps.canSetExpectedPrice
                  ? {
                      withVat: i18n.t('entity.vehicle.costs.disabledExpectedPriceWithVAT'),
                      withoutVat: i18n.t('entity.vehicle.costs.disabledExpectedPriceWithoutVAT'),
                      vatType: i18n.t('entity.vehicle.costs.disabledExpectedPriceVATType'),
                    }
                  : undefined
              }
              labels={{
                withVat: i18n.t('entity.vehicle.costs.expectedPriceWithVAT'),
                withoutVat: i18n.t('entity.vehicle.costs.expectedPriceWithoutVAT'),
              }}
            />
            <Box width="100%">
              <Button
                variant="link"
                title={i18n.t('entity.vehicle.costs.calculateFinancing')}
                leftIcon="editor/attach_money"
                onClick={() => {
                  openDialog(
                    <CalculateFinancingForm
                      data-testid={otherProps['data-testid']}
                      onDiscard={() => {
                        closeCurrentDialog();
                      }}
                      defaultFinancedValue={
                        toNumber(otherProps.saleVehicle?.purchasePrice.withoutVat?.amount) ??
                        undefined
                      }
                      onSubmit={(financingCost) => {
                        const {expectedPrice} = formApi.getValues();
                        if (!expectedPrice || !expectedPrice.vatType) {
                          showNotification.error(i18n.t('general.notifications.error'));
                          return;
                        }
                        calculatePriceWithoutVat({
                          amount: String(financingCost),
                          vatRateType: expectedPrice?.vatType as VatRateType,
                          countryCode: otherProps.countryCode,
                        })
                          .unwrap()
                          .then((res) => {
                            formApi.setValue(
                              'expectedPrice',
                              {
                                ...expectedPrice,
                                withoutVat: {
                                  amount: res.withoutVat,
                                  currency: otherProps.currency,
                                },
                                withVat: {
                                  amount: res.withVat,
                                  currency: otherProps.currency,
                                },
                              } as BaseTransactionFormSchemaType['expectedPrice'],
                              {
                                shouldDirty: true,
                                shouldTouch: true,
                              }
                            );
                            closeCurrentDialog();
                          })
                          .catch(handleApiError);
                      }}
                    />,
                    {
                      title: i18n.t('entity.vehicle.costs.expectedFinancingCost'),
                      size: 'small',
                    }
                  );
                }}
              />
            </Box>
            <ButtonGroup align="right">
              <Show when={otherProps.onDiscard}>
                <FormButton
                  variant="secondary"
                  title={i18n.t('general.actions.discard')}
                  onClick={otherProps.onDiscard}
                  control={control}
                  data-testid={suffixTestId('discard', otherProps)}
                />
              </Show>
              <FormButton
                type="submit"
                variant="primary"
                control={control}
                title={isEditForm ? i18n.t('general.actions.save') : i18n.t('general.actions.add')}
                isLoading={isCalculatingVat}
                data-testid={suffixTestId('submit', otherProps)}
              />
            </ButtonGroup>
          </VStack>
        );
      }}
    </Form>
  );
}
